"use client";

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import {
  ChatWidget,
  ReactionWidget,
  RollWidget,
  RollWidgetFrame,
  type ChatMessage,
  type ChatModeration,
} from "@/components/roll-widgets";
import { useLiveSession } from "@/contexts/live-session-context";
import { useHandheld } from "@/hooks/use-short-viewport";
import type { LiveRecitalRecording } from "@/lib/api/live-api";
import type { HallMessage, LiveHall } from "@/lib/live/hall";
import type { LiveParticipantsReply } from "@/lib/live/session-control";
import { useLiveParticipants } from "@/lib/live/use-live-participants";
import { CameraControl } from "./camera-control";
import { AudienceList, RecitalAudience, audienceCountOf, type AudienceModeration } from "./recital-audience";
import { MeasureLinesSwitch, RecitalInvite, TakeControl } from "./recital-controls";

/**
 * Largeur des trois widgets. Le QR code de l'appairage fait 176 px à lui seul,
 * et un nom de spectateur tient mal sous 240.
 */
const STAGE_WIDTH_PX = 268;
const CAMERA_WIDTH_PX = 296;
const HALL_WIDTH_PX = 288;
const CURTAIN_WIDTH_PX = 320;

/** Au-delà, la salle défile plutôt que de descendre jusqu'au clavier. */
const HALL_LIST_MAX_PX = 208;

/**
 * Le relevé des participants hors du fournisseur live : rien à demander.
 * Déclaré une fois, pour que le relevé ne se relance pas à chaque rendu.
 */
async function noParticipants(): Promise<LiveParticipantsReply | null> {
  return null;
}

/**
 * La console du récital, posée sur le rouleau.
 *
 * # Pourquoi elle a quitté le bandeau
 *
 * Elle tenait dans un panneau de 380 px qu'il fallait ouvrir par la barre, et
 * qui **recouvrait le rouleau** une fois ouvert : on pilotait un récital en
 * cachant ce que le récital montre. Les widgets (`RollWidget`) rendent les deux
 * à la fois — les commandes sont là, et le rouleau aussi —, et ils se posent
 * dans les coins que le parcours laisse libres.
 *
 * Le bandeau ne disparaît pas : il garde les mesures de mise au point (voir
 * `LiveSessionPanel`), qui ne sont pas des commandes, et il reste l'écran du
 * spectateur, qui n'en a aucune.
 *
 * # Trois widgets, et ce qui les sépare
 *
 * - **Récital** en haut à gauche : ce qui se commande — ouvrir au public,
 *   l'enregistrement, la prise, la trame, et la sortie.
 * - **Caméra** en haut à droite : l'instrument, qu'on branche et qu'on cadre.
 * - **La salle** en bas à droite : qui est là, et comment on y entre.
 *
 * Les vignettes vidéo gardent le bas à gauche (`RecitalCamera`), et le haut au
 * centre reste à la capsule du parcours.
 *
 * Un enregistrement sans public n'a pas de salle : ni lien, ni code, ni
 * personne à compter. Son coin gauche porte un interrupteur plutôt qu'une porte
 * — voir {@link RecordWidget}.
 *
 * # Presque rien pour un spectateur
 *
 * Il ne pilote rien : ni les portes, ni la caméra, ni la trame — le service
 * refuse déjà ces gestes. Il garde le bandeau, qui est fermé par défaut et ne
 * lui prend donc aucun pixel de ce qu'il est venu regarder. C'est aussi ce qui
 * garde le relevé de salle économe : il tourne chez l'interprète, un par
 * récital, et non chez chacun des spectateurs.
 *
 * Une seule chose lui est due sur le rouleau : le mot du rideau. Quand plus
 * rien n'arrive, un écran muet se lit comme une panne — voir
 * {@link CurtainNotice}.
 */
export function RecitalWidgets() {
  const live = useLiveSession();
  const recital = live?.session?.recital ?? null;

  if (live === null || recital === null) {
    return null;
  }

  if (live.isListener) {
    return (
      <>
        <RollWidget anchor="top-center">
          <CurtainNotice show={live.curtain} />
        </RollWidget>

        <RecitalChat />
        <RecitalHearts tapping />
      </>
    );
  }

  return (
    <>
      <RollWidget anchor="top-left">
        {recital.recordingOnly ? <RecordWidget /> : <StageWidget />}
      </RollWidget>

      {/* La salle parle à l'interprète aussi : le fil, replié tant qu'il joue,
          et ses cœurs qui montent depuis la salle. Sans public, ni l'un ni
          l'autre : personne à entendre. */}
      {!recital.recordingOnly && <RecitalChat />}
      {!recital.recordingOnly && <RecitalHearts />}

      <RollWidget anchor="top-right">
        <RollWidgetFrame kicker="Caméra" width={CAMERA_WIDTH_PX}>
          <div className="px-4 py-3">
            <CameraControl />
          </div>
        </RollWidgetFrame>
      </RollWidget>

      {!recital.recordingOnly && (
        <RollWidget anchor="bottom-right">
          <HallWidget />
        </RollWidget>
      )}
    </>
  );
}

/**
 * Le récital devant un public : la porte, l'enregistrement, la prise, la trame.
 *
 * # Ce que la porte sépare
 *
 * Monter sur scène et ouvrir sont deux gestes (décidé le 2026-09-18). Entre les
 * deux, l'interprète branche son téléphone et se cadre : le public attend
 * derrière la porte, et rien ne s'enregistre. Ce second geste tombe donc là où
 * il est déjà — dans le studio, sous sa caméra — et pas dans une page qu'il
 * aurait quittée. Il dit ce qu'il déclenche : on n'ouvre pas sa salle par
 * mégarde.
 *
 * # Ce qui n'y est pas
 *
 * Lecture et pause. Elles sont dans la barre pour toute séance, et le panneau
 * en avait un doublon ; c'est celui-ci qu'on retire, pas l'autre. Le message
 * d'une mise en marche refusée, lui, reste — sans lui, un récital figé
 * n'offrirait plus aucune explication une fois le bandeau vidé.
 *
 * La trame se règle des deux côtés de la porte : c'est le décor de ce qu'on va
 * jouer, et on le pose en se préparant, comme on cadre sa caméra.
 */
function StageWidget() {
  const live = useLiveSession();
  const [leaving, setLeaving] = useState(false);

  if (live === null) {
    return null;
  }

  const open = live.doorsOpen === true;

  return (
    <RollWidgetFrame
      kicker="Récital"
      width={STAGE_WIDTH_PX}
      ariaLabel="Commandes du récital"
      trailing={open ? <OnAirTag curtain={live.curtain} /> : undefined}
      footer={
        <LeaveButton
          armed={leaving}
          onArm={setLeaving}
          onLeave={live.leave}
          // Avant l'ouverture, rien n'a eu lieu : le récital redevient à venir,
          // et se remontera quand on voudra. Après, on arrête quelque chose.
          cta={open ? "Arrêter le récital" : "Quitter la scène"}
          question={
            open
              ? "Arrêter le récital ? La salle se vide, et il ne rouvrira pas."
              : "Quitter la scène ? Le récital redeviendra à venir."
          }
          confirm={open ? "Oui, arrêter" : "Oui, quitter"}
        />
      }
    >
      <div className="flex flex-col gap-2 px-4 py-3">
        {!open && <Doors onOpen={live.openDoors} />}

        {open && <CurtainSwitch />}

        {open && live.recording !== null && live.recording !== "off" && (
          <RecordingMark recording={live.recording} onStop={live.stopRecording} />
        )}

        {/* Le découpage ne se propose que pendant qu'on enregistre : arrêté, il
            n'y a plus rien à couper. */}
        {open && live.recording === "on" && (
          <TakeControl take={live.take} mine={live.isLeader} onNext={live.nextTake} />
        )}

        <MeasureLinesSwitch hidden={live.measuresHidden} onChange={live.setMeasuresHidden} />

        {live.startFailure !== null && (
          <p role="alert" className="m-0 text-[12px] leading-[1.45] text-red-300">
            {live.startFailure}
          </p>
        )}
      </div>
    </RollWidgetFrame>
  );
}

/**
 * Ce que la salle reçoit, dans l'en-tête : « en direct », ou « rideau ».
 *
 * La porte ouverte et le rideau levé sont deux choses, et l'en-tête ne dit que
 * la seconde une fois la première franchie : baissé, le rideau est le fait le
 * plus important de l'écran, et une pastille qui continuerait de battre
 * « en direct » derrière lui serait le mensonge à ne pas faire.
 */
function OnAirTag({ curtain }: { curtain: boolean }) {
  if (curtain) {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-300">
        <span aria-hidden className="size-1.5 rounded-full bg-amber-400" />
        rideau
      </span>
    );
  }

  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-red-500/10 px-2 py-0.5 text-[10px] text-red-300">
      <span aria-hidden className="relative flex size-1.5">
        <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-60 motion-safe:animate-ping" />
        <span className="relative inline-flex size-1.5 rounded-full bg-red-400" />
      </span>
      en direct
    </span>
  );
}

/**
 * Le rideau : ce qui sort de la scène, ou ne sort plus.
 *
 * # Ce que ce n'est pas
 *
 * La pause. Elle fige la grille de temps de toute la salle, l'interprète
 * compris : c'est un entracte pour tout le monde, et elle est dans la barre. Le
 * rideau ne touche pas au temps — il continue de couler, on joue, on voit son
 * rouleau avancer, on cadre une deuxième caméra — et c'est seulement la
 * diffusion qui s'arrête. Demandé par l'utilisateur le 2026-09-20 dans ces
 * termes : « les autres ne doivent rien voir mais lui doit tout voir ».
 *
 * # Ce qu'il coupe, et où
 *
 * Tout : les notes et les caméras. Rien n'est coupé ici, cela dit — le geste va
 * au service, qui publie un fait de séance (`curtain`), et chacun s'y range de
 * son côté : l'émetteur se tait, le récepteur écarte ce qui serait encore en
 * vol, un téléphone appairé sort sa caméra de la salle. C'est ce qui rend le
 * rideau vrai pour un spectateur dont le client serait plus ancien que lui, et
 * ce qui permet à un téléphone de l'apprendre sans qu'on ait de canal vers lui.
 *
 * L'aperçu de la webcam de ce poste, lui, reste : la piste sort de la salle
 * sans s'éteindre. Un téléphone se cadre sur son propre écran.
 *
 * # Pourquoi il coupe la prise
 *
 * Parce que ce qui se joue derrière le rideau n'appartient pas au récital.
 * L'enregistreur est un participant comme un autre : il ne reçoit plus rien, et
 * la prise ouverte à la chute reste vide. Le lecteur de replay écarte déjà les
 * prises sans note, donc l'entracte ne se rejoue pas — sans qu'il ait fallu
 * apprendre quoi que ce soit à l'enregistreur.
 */
function CurtainSwitch() {
  const live = useLiveSession();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [uncut, setUncut] = useState(false);

  const down = live?.curtain === true;

  const toggle = useCallback(async () => {
    if (live === null) {
      return;
    }

    setBusy(true);
    setFailed(false);
    setUncut(false);

    // Une prise s'ouvre de chaque côté du rideau : celle d'avant se ferme là où
    // la salle a cessé de recevoir, celle d'après commence là où elle reçoit de
    // nouveau. Celle du milieu n'aura aucune note, et disparaîtra d'elle-même du
    // replay. Le service refuse (403) la coupe demandée par qui ne mène pas la
    // séance : on ne la tente pas, plutôt que d'annoncer une panne.
    const cut = live.recording === "on" && live.isLeader;

    try {
      if (down) {
        // Couper d'abord : la prise neuve doit exister avant que quoi que ce
        // soit reparte, sinon les premières notes tombent dans l'entracte.
        setUncut(cut && !(await live.nextTake()));
        setFailed(!(await live.setCurtain(false)));

        return;
      }

      // Baisser d'abord : c'est l'urgence du geste. La coupe suit, à quelques
      // millisecondes de là, et le silence est déjà tombé.
      setFailed(!(await live.setCurtain(true)));
      setUncut(cut && !(await live.nextTake()));
    } finally {
      setBusy(false);
    }
  }, [down, live]);

  if (live === null) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => void toggle()}
        disabled={busy}
        aria-pressed={down}
        className={`flex h-11 w-full cursor-pointer items-center gap-3 rounded-lg px-3 text-left transition-colors duration-150 disabled:cursor-wait disabled:opacity-60 ${
          down ? "bg-amber-500/10 hover:bg-amber-500/15" : "bg-bg-elevated hover:bg-bg-hover"
        }`}
      >
        {/* Un rideau qui tombe, puis un rideau tombé : le glyphe dit l'état, et
            non le clic — ici les deux se lisent d'un coup, parce que le mot
            juste à côté dit l'autre moitié. */}
        <span
          aria-hidden
          className={`grid h-6 w-6 shrink-0 place-items-center overflow-hidden rounded-[5px] border ${
            down ? "border-amber-400/40 bg-amber-500/10" : "border-border-subtle bg-white/[0.04]"
          }`}
        >
          <span
            className={`w-full transition-all duration-200 ${
              down ? "h-full bg-amber-400/70" : "h-[5px] bg-text-muted/60"
            }`}
          />
        </span>

        <span className="flex min-w-0 flex-1 flex-col">
          <span
            className={`truncate text-[13px] font-semibold ${down ? "text-amber-200" : "text-text-primary"}`}
          >
            {down ? "Rideau baissé" : "À l'antenne"}
          </span>
          <span className="truncate text-[11px] text-text-muted">
            {down ? "Relever pour reprendre" : "Baisser le rideau"}
          </span>
        </span>
      </button>

      {down && (
        <p className="m-0 text-[12px] leading-snug text-text-secondary">
          La salle ne reçoit plus rien. Tu peux jouer, cadrer, brancher une caméra&nbsp;: ton aperçu reste.
        </p>
      )}

      {failed && (
        <p role="alert" className="m-0 text-[12px] text-red-300">
          Le rideau n&apos;a pas bougé. Réessaie.
        </p>
      )}

      {/* Le rideau, lui, a bougé : c'est la coupe qui n'a pas pris, et ça ne se
          dit pas avec les mêmes mots — l'entracte restera dans la prise, et
          rien d'autre n'est cassé. */}
      {uncut && !failed && (
        <p role="alert" className="m-0 text-[12px] text-amber-300">
          La prise n&apos;a pas été coupée&nbsp;: cet entracte restera dans l&apos;enregistrement.
        </p>
      )}
    </div>
  );
}

/** Le geste qui fait entrer le public, et l'enregistrement avec lui. */
function Doors({ onOpen }: { onOpen: () => Promise<boolean> }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const open = useCallback(() => {
    setBusy(true);
    setFailed(false);

    void onOpen()
      .then((ok) => setFailed(!ok))
      .finally(() => setBusy(false));
  }, [onOpen]);

  return (
    <>
      <p className="m-0 text-[12px] leading-snug text-text-secondary">
        Tu es seul·e sur scène. Branche ta caméra, cadre-la&nbsp;: rien n&apos;est enregistré et personne
        n&apos;est encore entré.
      </p>

      <button
        type="button"
        onClick={open}
        disabled={busy}
        className="h-9 w-full cursor-pointer rounded-lg bg-accent text-[12px] font-semibold text-bg-deep transition-colors duration-150 hover:bg-accent-strong disabled:opacity-60"
      >
        {busy ? "· · ·" : "Ouvrir au public"}
      </button>

      {failed && (
        <p role="alert" className="m-0 text-[11px] text-red-300">
          L&apos;ouverture n&apos;a pas abouti. Réessaie dans un instant.
        </p>
      )}
    </>
  );
}

/**
 * L'enregistrement du récital, chez l'interprète : la marque, et l'arrêt.
 *
 * Décidé le 2026-09-17 : l'interprète voit qu'il est enregistré, et peut
 * arrêter. L'arrêt est définitif — rien ne le relance, pas même son retour sur
 * scène —, d'où un second geste explicite, comme pour annuler un récital dans
 * la salle d'attente. Quand l'enregistreur n'a pas accepté de commencer, la
 * marque ne ment pas.
 *
 * Sans public, cette marque n'existe pas : l'enregistrement est tout le
 * récital, et c'est l'interrupteur de {@link RecordWidget} qui le porte.
 */
function RecordingMark({
  recording,
  onStop,
}: {
  recording: Exclude<LiveRecitalRecording, "off">;
  onStop: () => Promise<boolean>;
}) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  if (recording === "stopped") {
    return (
      <p className="m-0 text-[12px] leading-[1.45] text-text-muted">
        Enregistrement arrêté. Ce qui a été joué avant est gardé.
      </p>
    );
  }

  if (recording === "unavailable") {
    return (
      <p role="alert" className="m-0 text-[12px] leading-[1.45] text-amber-300">
        L&apos;enregistrement n&apos;a pas pu commencer. Le récital continue sans lui.
      </p>
    );
  }

  if (recording === "full") {
    return (
      <p role="alert" className="m-0 text-[12px] leading-[1.45] text-amber-300">
        Ton forfait n&apos;a plus de place pour un enregistrement : ce récital ne sera pas gardé. Le récital continue.
      </p>
    );
  }

  const stop = async () => {
    setBusy(true);
    setFailed(false);

    const stopped = await onStop();

    setBusy(false);
    setArmed(false);
    setFailed(!stopped);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-10 items-center gap-2 rounded-lg bg-bg-elevated px-3">
        <span aria-hidden className="size-2 shrink-0 animate-pulse rounded-full bg-red-500" />
        <span className="flex-1 text-[12px] font-medium text-text-primary">Enregistré</span>

        {!armed && (
          <button
            type="button"
            onClick={() => setArmed(true)}
            className="cursor-pointer text-[11px] font-semibold text-red-300 hover:text-red-200"
          >
            Arrêter
          </button>
        )}
      </div>

      {armed && (
        <div className="flex flex-col gap-2 rounded-lg border border-red-500/25 px-3 py-2.5">
          <p className="m-0 text-[12px] leading-[1.45] text-text-secondary">
            Arrêter l&apos;enregistrement&nbsp;? Il ne reprendra pas.
          </p>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => void stop()}
              disabled={busy}
              className="h-7 cursor-pointer rounded-md bg-red-500/10 px-2.5 text-[12px] font-semibold text-red-300 hover:bg-red-500/15 disabled:cursor-default disabled:opacity-50"
            >
              {busy ? "· · ·" : "Oui, arrêter"}
            </button>
            <button
              type="button"
              onClick={() => setArmed(false)}
              disabled={busy}
              className="h-7 cursor-pointer rounded-md px-2.5 text-[12px] text-text-secondary hover:bg-white/[0.06]"
            >
              Non
            </button>
          </div>
        </div>
      )}

      {failed && (
        <p role="alert" className="m-0 text-[12px] text-red-300">
          L&apos;arrêt n&apos;a pas abouti. Réessaie.
        </p>
      )}
    </div>
  );
}

/**
 * La salle : qui est là, et comment on y entre.
 *
 * # Pourquoi elle est sur le rouleau
 *
 * Parce que c'est la seule chose de l'écran qui change toute seule, et la seule
 * que l'interprète regarde **entre** deux morceaux : trois personnes arrivées
 * pendant qu'il jouait, c'est l'information qui fait le récital. Derrière un
 * panneau fermé, elle n'existait qu'au moment où l'on pensait à aller la
 * chercher.
 *
 * Le lien et le code vivent en pied du même cadre, parce qu'une salle vide est
 * exactement le moment où l'on envoie le lien.
 *
 * # Ce que coûte le relevé
 *
 * Le panneau ne demandait la liste que pendant qu'il était ouvert
 * (`useLiveParticipants`), pour ne pas facturer au service une requête toutes
 * les trois secondes **par participant**. Le widget, lui, tourne tout le
 * récital — mais chez l'interprète seul, qui est unique : une requête toutes
 * les trois secondes par récital, et non par spectateur. C'est le même calcul,
 * et il retombe du bon côté.
 *
 * La fiche de l'interprète n'y est pas : c'est soi, et ce qu'elle dirait de son
 * état — joue, en pause — se lit déjà dans la barre.
 */
function HallWidget() {
  const live = useLiveSession();
  const lines = useLiveParticipants(
    live?.fetchParticipants ?? noParticipants,
    live?.room ?? null,
    live !== null,
  );
  const hearts = useHeartsTotal(live?.hall ?? null);
  const moderation = useAudienceModeration(live?.hall ?? null, live !== null && !live.isListener);

  if (live === null) {
    return null;
  }

  return (
    <RollWidgetFrame
      kicker="La salle"
      width={HALL_WIDTH_PX}
      ariaLabel="La salle du récital"
      trailing={
        <span className="flex shrink-0 items-center gap-2 text-[11px] tabular-nums text-text-muted">
          {hearts > 0 && <span style={{ color: "#fb7185" }}>♥&nbsp;{hearts}</span>}
          {lines !== null && <span>{audienceCountOf(lines.participants, live.headcount)}</span>}
        </span>
      }
      footer={<RecitalInvite />}
    >
      <div
        className="flex flex-col gap-2 overflow-y-auto px-4 py-3"
        style={{
          maxHeight: HALL_LIST_MAX_PX,
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(255,255,255,0.12) transparent",
        }}
      >
        <AudienceList
          identity={live.identity}
          lines={lines?.participants ?? null}
          headcount={live.headcount}
          moderation={moderation}
        />
      </div>
    </RollWidgetFrame>
  );
}

/**
 * Le bouton qui enregistre, la prise, la trame, et la sortie — sans public.
 *
 * # Un interrupteur, et non deux gestes
 *
 * « Commencer l'enregistrement » était un bouton qui disparaissait une fois
 * pressé : rien ne disait plus, ensuite, qu'on enregistrait — sinon une pastille
 * rouge dans un panneau fermé. Ici l'état est le bouton : allumé, il enregistre ;
 * éteint, il attend. C'est l'idiome de tous les enregistreurs, et c'est aussi le
 * seul endroit de l'écran où cette vérité-là doit se lire sans réfléchir.
 *
 * # Ce que l'extinction fait vraiment
 *
 * Elle **fige la séance** (`setTransportPaused`), et rien d'autre : l'enregistreur
 * reste dans la salle. C'est voulu, et c'est ce que le découpage en prises attend
 * — la prise en cours garde sa fin là où la musique s'est arrêtée, pas là où l'on
 * a repris dix minutes plus tard (voir `docs/spec/recorder.md`, « Les prises »).
 * Rallumer ouvre donc la **prise suivante** : ce qui s'est passé entre les deux
 * n'appartient à aucune prise qu'on rejoue, et le lecteur de replay ignore déjà
 * les prises sans note.
 *
 * L'arrêt définitif de l'enregistreur, lui, est le geste de sortie : il finalise
 * et dépose. Un enregistrement sans public n'existe que pour ça, et c'est
 * pourquoi « Quitter » et « terminer » sont ici le même bouton.
 *
 * # La reprise, contre la lecture de la barre
 *
 * La barre remet la séance en marche sans couper : c'est une respiration au
 * milieu d'une prise. Ce bouton-ci coupe. Les deux se distinguent par ce
 * qu'ils laissent derrière eux, pas par ce qu'ils font au temps.
 */
function RecordWidget() {
  const live = useLiveSession();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // Avant l'ouverture des portes, l'enregistreur n'est pas entré : la séance
  // tourne déjà (elle démarre en rejoignant la salle), mais rien n'est gardé.
  const open = live?.doorsOpen === true;
  const paused = live?.sessionState?.paused ?? null;
  const recording = open && paused === false;

  const toggle = useCallback(async () => {
    if (live === null) {
      return;
    }

    setBusy(true);
    setFailed(false);

    try {
      if (recording) {
        setFailed(!(await live.setTransportPaused(true)));

        return;
      }

      if (!open) {
        // Les portes ouvrent l'enregistrement : l'enregistreur entre, la
        // première prise court déjà. Rien à couper.
        setFailed(!(await live.openDoors()));

        return;
      }

      // On repart : la prise d'avant se ferme ici, à la position où la musique
      // s'est arrêtée, et la suivante s'ouvre avec le temps.
      const cut = await live.nextTake();

      setFailed(!(await live.setTransportPaused(false)) || !cut);
    } finally {
      setBusy(false);
    }
  }, [live, open, recording]);

  if (live === null) {
    return null;
  }

  return (
    <RollWidgetFrame
      kicker="Enregistrement"
      width={STAGE_WIDTH_PX}
      ariaLabel="Commandes de l'enregistrement"
      footer={
        <LeaveButton
          armed={leaving}
          onArm={setLeaving}
          onLeave={live.leave}
          cta="Quitter"
          question="Terminer l'enregistrement ? Il ne reprendra pas."
          confirm="Oui, terminer"
        />
      }
    >
      <div className="flex flex-col gap-2 px-4 py-3">
        <button
          type="button"
          onClick={() => void toggle()}
          disabled={busy}
          aria-pressed={recording}
          className={`flex h-11 w-full cursor-pointer items-center gap-3 rounded-lg px-3 text-left transition-colors duration-150 disabled:cursor-wait disabled:opacity-60 ${
            recording ? "bg-red-500/10 hover:bg-red-500/15" : "bg-bg-elevated hover:bg-bg-hover"
          }`}
        >
          {/* Le glyphe dit ce que le clic fait — un carré arrête —, le libellé
              dit où l'on en est. Les deux ensemble, parce qu'un bouton
              d'enregistrement qui ne montrerait que l'un des deux se lit de
              travers une fois sur deux. */}
          <span
            aria-hidden
            className={`grid size-6 shrink-0 place-items-center rounded-full ${
              recording ? "bg-red-500/20" : "bg-red-500/15"
            }`}
          >
            <span
              className={`bg-red-500 ${recording ? "size-2.5 rounded-[2px]" : "size-3 animate-none rounded-full"}`}
            />
          </span>

          <span className="flex min-w-0 flex-1 items-center gap-2">
            <span
              className={`truncate text-[13px] font-semibold ${recording ? "text-red-200" : "text-text-primary"}`}
            >
              {recording ? "Enregistrement" : open ? "En attente" : "Enregistrer"}
            </span>

            {recording && (
              <span aria-hidden className="size-1.5 shrink-0 animate-pulse rounded-full bg-red-500" />
            )}
          </span>
        </button>

        {/* Ce que le bouton ne dit pas : ce qu'il y a à faire avant, et ce
            qu'on gagne à couper. Une ligne chacun, et rien tant qu'on
            enregistre — à ce moment-là, on joue. */}
        {!open && (
          <p className="m-0 text-[12px] leading-snug text-text-secondary">
            Personne ne te regarde. Branche ta caméra, cadre-la, et lance l&apos;enregistrement quand tu veux.
          </p>
        )}

        {open && !recording && (
          <p className="m-0 text-[12px] leading-snug text-text-secondary">
            En pause. Reprendre ouvre une nouvelle prise.
          </p>
        )}

        {live.recording === "unavailable" && (
          <p role="alert" className="m-0 text-[12px] leading-[1.45] text-amber-300">
            L&apos;enregistreur n&apos;a pas pu commencer. Ce que tu joues ne sera pas gardé.
          </p>
        )}

        {live.recording === "full" && (
          <p role="alert" className="m-0 text-[12px] leading-[1.45] text-amber-300">
            Ton forfait n&apos;a plus de place pour un enregistrement : ce que tu joues ne sera pas gardé. Libère-en
            dans « Mes enregistrements ».
          </p>
        )}

        {failed && (
          <p role="alert" className="m-0 text-[12px] text-red-300">
            Le geste n&apos;a pas abouti. Réessaie.
          </p>
        )}

        {/* La prise n'a de sens qu'une fois l'enregistreur entré : avant, il n'y
            a rien à découper. */}
        {open && <TakeControl take={live.take} mine={live.isLeader} onNext={live.nextTake} />}

        {/* La trame, elle, se règle **avant** de lancer : c'est le décor de ce
            qu'on va enregistrer, et on le pose en se préparant, comme on cadre
            sa caméra. La découvrir une fois l'enregistrement parti obligerait à
            couper une prise pour changer un décor. */}
        <MeasureLinesSwitch hidden={live.measuresHidden} onChange={live.setMeasuresHidden} />
      </div>
    </RollWidgetFrame>
  );
}

/**
 * La porte de sortie, en deux temps.
 *
 * Sortir termine le récital : la salle se vide, l'enregistreur finalise et
 * dépose, et rien ne relance l'un ni l'autre. Le bouton du bandeau pouvait se
 * permettre un seul clic — il fallait déjà ouvrir le bandeau pour l'atteindre.
 * Posé sur le rouleau, il est sous la main pendant qu'on joue, et un clic de
 * trop coûterait la séance.
 *
 * La question est écrite en entier, sur sa ligne : « Arrêter ? » tiendrait à
 * côté des deux boutons, mais un geste irréversible se relit, et ce qu'on perd
 * ne se devine pas au verbe.
 */
function LeaveButton({
  armed,
  onArm,
  onLeave,
  cta,
  question,
  confirm,
}: {
  armed: boolean;
  onArm: (armed: boolean) => void;
  onLeave: () => Promise<void> | void;
  /** Le mot du bouton au repos : ce qu'on va faire. */
  cta: string;
  /** La question, une fois armé : ce qu'on perd. */
  question: string;
  confirm: string;
}) {
  if (!armed) {
    return (
      <button
        type="button"
        onClick={() => onArm(true)}
        className="h-7 w-full cursor-pointer rounded-md text-[12px] font-semibold text-text-secondary transition-colors duration-150 hover:bg-white/[0.06] hover:text-text-primary"
      >
        {cta}
      </button>
    );
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <p className="m-0 text-[12px] leading-[1.45] text-text-secondary">{question}</p>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void onLeave()}
          className="h-7 shrink-0 cursor-pointer rounded-md bg-red-500/10 px-2.5 text-[12px] font-semibold text-red-300 transition-colors duration-150 hover:bg-red-500/15"
        >
          {confirm}
        </button>

        <button
          type="button"
          onClick={() => onArm(false)}
          className="h-7 shrink-0 cursor-pointer rounded-md px-2.5 text-[12px] text-text-secondary transition-colors duration-150 hover:bg-white/[0.06]"
        >
          Non
        </button>
      </div>
    </div>
  );
}

/**
 * « L'interprète a baissé le rideau », chez le spectateur.
 *
 * # Pourquoi il faut le dire
 *
 * Parce que le rideau ne se voit pas : les notes cessent d'arriver, les
 * vignettes disparaissent, le rouleau continue de défiler vide. C'est
 * exactement à quoi ressemble une connexion tombée, et c'est la lecture que
 * quelqu'un fera s'il n'y a rien d'écrit. Un mot, et le même écran devient une
 * attente.
 *
 * Au centre du haut : c'est la place de la capsule du parcours, qu'un
 * spectateur n'a pas — il ne travaille pas, il regarde. Le mot y est donc chez
 * lui, au-dessus du rouleau, sans rien recouvrir de ce qui l'intéresse.
 *
 * Il ne dit pas combien de temps. Personne ne le sait, l'interprète le premier,
 * et une durée annoncée qu'on dépasse est pire que pas de durée du tout.
 */
function CurtainNotice({ show }: { show: boolean }) {
  if (!show) {
    return null;
  }

  return (
    <RollWidgetFrame kicker="Entracte" width={CURTAIN_WIDTH_PX} ariaLabel="Le récital est en pause">
      <p className="m-0 px-4 py-3 text-[12px] leading-[1.45] text-text-secondary">
        L&apos;interprète a baissé le rideau. Le récital n&apos;est pas terminé&nbsp;: reste, ça repart
        d&apos;ici.
      </p>
    </RollWidgetFrame>
  );
}

const NO_MESSAGES: readonly HallMessage[] = [];

function noSubscribe(): () => void {
  return () => undefined;
}

/**
 * Le fil de la salle, sur le rouleau : chez le spectateur comme chez
 * l'interprète.
 *
 * # Replié chez qui joue, et sur un téléphone
 *
 * L'interprète regarde son rouleau ; un panneau de trois cents pixels par-dessus
 * pendant qu'il joue serait un prix pour une chose qu'il lit **entre** deux
 * morceaux. La bulle compte ce qu'il n'a pas lu, et c'est lui qui l'ouvre. Sur
 * un téléphone couché, même règle et même raison : le rouleau n'a que 260 px
 * de haut.
 *
 * # La salle en colonne, seulement en avant
 *
 * Mis en avant, le fil montre la salle à sa gauche — l'interprète, puis qui
 * écoute. Le relevé ne tourne que pendant ce temps (`useLiveParticipants`,
 * `ahead`) : une requête toutes les trois secondes par spectateur qui aurait
 * le chat ouvert serait le prix d'une liste que personne ne regarde.
 *
 * # Ce qu'on ne garde pas
 *
 * Rien. Le fil est celui qu'on a vu passer depuis qu'on est là (`LiveHall`), et
 * un rechargement repart de zéro.
 *
 * # La modération, chez l'interprète
 *
 * Les gestes vont au service par la salle (`LiveHall`), et ce qu'ils ont fait
 * revient par elle. La marque « sourdine » d'un auteur vient d'abord du geste
 * qu'on vient de faire (`mutedIdentities`), puis du relevé — qui ne tourne
 * qu'en avant : un message d'avant le geste, sur un fil replié, se lit sans
 * marque, et c'est sans conséquence, le menu offrant l'autre sens.
 */
function RecitalChat() {
  const live = useLiveSession();
  const handheld = useHandheld();
  const hall = live?.hall ?? null;
  const moderating = live !== null && !live.isListener;
  const [ahead, setAhead] = useState(false);

  const subscribe = useMemo(() => (hall === null ? noSubscribe : (listener: () => void) => hall.subscribe(listener)), [hall]);
  const snapshot = useMemo(() => (hall === null ? () => NO_MESSAGES : () => hall.snapshot()), [hall]);
  const held = useSyncExternalStore(subscribe, snapshot, () => NO_MESSAGES);
  const muted = useSyncExternalStore(subscribe, () => hall?.muted ?? false, () => false);
  const mutedOverride = useSyncExternalStore(subscribe, () => hall?.mutedIdentities ?? NO_MUTES, () => NO_MUTES);

  const lines = useLiveParticipants(
    live?.fetchParticipants ?? noParticipants,
    live?.room ?? null,
    ahead && live !== null,
  );

  const messages = useMemo<readonly ChatMessage[]>(
    () =>
      held.map((message) => ({
        id: message.id,
        at: message.at,
        text: message.text,
        author: {
          id: message.from.identity,
          name: message.from.name,
          role: message.from.canPlay ? "performer" : "spectator",
          ...(moderating
            ? {
                muted:
                  mutedOverride.get(message.from.identity) ??
                  lines?.participants.some((line) => line.identity === message.from.identity && line.muted) ??
                  false,
              }
            : {}),
        },
      })),
    [held, lines, moderating, mutedOverride],
  );

  const send = useCallback((text: string) => (hall === null ? Promise.resolve("failed" as const) : hall.say(text)), [hall]);

  const setChatClosed = live?.setChatClosed;
  const moderation = useMemo<ChatModeration | undefined>(
    () =>
      hall === null || !moderating || setChatClosed === undefined
        ? undefined
        : {
            setClosed: setChatClosed,
            retract: (id) => hall.retract(id),
            mute: (identity, isMuted) => hall.mute(identity, isMuted),
            exclude: (identity) => hall.exclude(identity),
          },
    [hall, moderating, setChatClosed],
  );
  const audienceModeration = useAudienceModeration(hall, moderating);

  if (live === null || hall === null) {
    return null;
  }

  return (
    <ChatWidget
      messages={messages}
      selfId={live.identity ?? ""}
      onSend={send}
      audience={live.headcount}
      collapsedByDefault={!live.isListener || handheld}
      onAhead={setAhead}
      closed={live.chatClosed}
      muted={muted}
      moderation={moderation}
      aside={
        <RecitalAudience
          identity={live.identity}
          lines={lines?.participants ?? null}
          headcount={live.headcount}
          paused={live.sessionState?.paused ?? null}
          moderation={audienceModeration}
        />
      }
    />
  );
}

const NO_MUTES: ReadonlyMap<string, boolean> = new Map();

/**
 * Les gestes de l'interprète sur la liste de la salle, et ce qu'il sait de
 * ses sourdines. `undefined` chez la salle : la liste n'y porte aucun menu.
 */
function useAudienceModeration(hall: LiveHall | null, moderating: boolean): AudienceModeration | undefined {
  const subscribe = useMemo(() => (hall === null ? noSubscribe : (listener: () => void) => hall.subscribe(listener)), [hall]);
  const mutedOverride = useSyncExternalStore(subscribe, () => hall?.mutedIdentities ?? NO_MUTES, () => NO_MUTES);

  return useMemo(
    () =>
      hall === null || !moderating
        ? undefined
        : {
            gestures: {
              mute: (identity, muted) => hall.mute(identity, muted),
              exclude: (identity) => hall.exclude(identity),
            },
            mutedOverride,
          },
    [hall, moderating, mutedOverride],
  );
}

/**
 * Les cœurs, en bas à droite : un bouton chez le spectateur, la pluie seule
 * chez l'interprète — elle monte depuis « La salle », qui est d'où ils viennent.
 */
function RecitalHearts({ tapping = false }: { tapping?: boolean }) {
  const live = useLiveSession();
  const hall = live?.hall ?? null;

  const hearts = useMemo(
    () => (hall === null ? noSubscribe : (listener: (count: number, total: number) => void) => hall.onHearts(listener)),
    [hall],
  );

  if (hall === null) {
    return null;
  }

  return <ReactionWidget hearts={hearts} onTap={tapping ? () => hall.tap() : undefined} order={1} />;
}

/**
 * Le total de cœurs de la salle, tel que le service le publie ; zéro tant
 * qu'aucun n'est arrivé. Rangé sous la salle qui l'a publié : une séance
 * neuve repart de zéro sans qu'un effet ait à remettre quoi que ce soit.
 */
function useHeartsTotal(hall: LiveHall | null): number {
  const [held, setHeld] = useState<{ hall: LiveHall | null; total: number }>({ hall: null, total: 0 });

  useEffect(() => {
    if (hall === null) {
      return;
    }

    return hall.onHearts((_count, published) => setHeld({ hall, total: published }));
  }, [hall]);

  return held.hall === hall ? held.total : 0;
}
